import type { TFunction } from "i18next";
import { FileSpreadsheet, Info } from "lucide-react";
import { lazy, Suspense, useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { AccordionItem } from "@krea/shared/design-system/AccordionItem";
import { Alert } from "@krea/shared/design-system/alerts/Alert";
import { Button } from "@krea/shared/design-system/Button";
import { Input } from "@krea/shared/design-system/input/input";
import { LabeledSlider } from "@krea/shared/design-system/LabeledSlider";
import { ResponsiveTooltip } from "@krea/shared/design-system/ResponsiveTooltip";
import { Switch } from "@krea/shared/design-system/Switch";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@krea/shared/design-system/Tabs";
import { Text } from "@krea/shared/design-system/Text";

import { buildRepaymentPlanExportSpec } from "./buildRepaymentPlanExportSpec";
import type { IRepaymentChartRow } from "./RepaymentPlanChart";
import type {
  IExtendedTermState,
  IRepaymentPlanBidInfo,
  IRepaymentPlanData,
} from "./types";
import { useRepaymentPlanDisplay } from "./useRepaymentPlanDisplay";

// Long enough that a multi-digit term is typed before anything fires, short
// enough that the admin never has to click away to see the result. Each
// commit is a network round trip whose resolver runs the rate solve.
const MONTHS_COMMIT_DEBOUNCE_MS = 500;

const RepaymentPlanChart = lazy(() =>
  import("./RepaymentPlanChart").then((m) => ({
    default: m.RepaymentPlanChart,
  })),
);

interface IRepaymentPlanSectionProps {
  bid: IRepaymentPlanBidInfo;
  // The caller decides whether to render this section at all (admin-gated, and
  // only when a plan exists), so by the time we render a plan is always present.
  plan: IRepaymentPlanData;
  currency: string;
  formatCurrency: (value: string | number) => string;
  // Whole-currency formatter (no decimals) for the chart's axis ticks.
  formatAxisCurrency: (value: string | number) => string;
  formatPaybackTime: (months: number) => string;
  companyName: string | null;
  // Used to name the exported file: amortizationplan_<loanApp>_offer_<lender>_<bid>.
  loanApplicationId: string | null;
  lenderName: string | null;
  /** KREA-4474: when present, an admin can simulate a longer term than the
   * lender quoted. Absent means the section behaves exactly as before. */
  extendedTerm?: IExtendedTermState;
}

const StatBox = ({
  label,
  value,
  tooltip,
}: {
  label: string;
  value: string;
  // Optional plain-language explanation; when present an info icon is shown
  // that reveals it (hover tooltip on desktop, bottom drawer on mobile).
  tooltip?: string;
}) => (
  <div className="flex min-w-[7rem] flex-1 flex-col gap-1 rounded-lg border border-gray-200 bg-white p-3">
    <div className="flex items-center gap-1">
      <Text className="text-label">{label}</Text>
      {tooltip && (
        <ResponsiveTooltip
          className="max-w-[16rem]"
          trigger={
            <button
              type="button"
              aria-label={label}
              className="inline-flex cursor-help items-center rounded-full text-gray-500 hover:text-gray-700 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-1"
            >
              <Info className="size-3.5" />
            </button>
          }
        >
          <p className="text-helper">{tooltip}</p>
        </ResponsiveTooltip>
      )}
    </div>
    <Text className="text-base-strong tabular-nums">{value}</Text>
  </div>
);

// KREA-4474: extracted out of renderBody, which tripped Biome's cognitive
// complexity gate (19 > 15) once this block's per-method conditionals were
// added inline. `plan` is always the lender's quoted offer -- never
// `activePlan`, which becomes the extended plan itself while the switch is
// on, which would make every delta zero.
const ExtendedCostIncreaseSummary = ({
  plan,
  extendedPlan,
  quotedMonths,
  isBullet,
  formatCurrency,
  t,
}: {
  plan: IRepaymentPlanData;
  extendedPlan: IRepaymentPlanData;
  quotedMonths: number;
  // BULLET/BUILDING_CREDIT: the monthly payment is interest-only and never
  // changes with term, so that box says nothing -- omit it and let extra
  // total cost lead instead.
  isBullet: boolean;
  formatCurrency: (value: string | number) => string;
  t: TFunction;
}) => {
  // extendedPlan's month-1 entry is guaranteed by the caller's `fullMonths >
  // 1` render guard (fullMonths follows activePlan, which IS extendedPlan
  // while the switch is on). plan's is expected too -- any real quoted offer
  // has at least one payment month -- but that isn't provable from
  // fullMonths, which says nothing about the quoted plan. Either way,
  // `noUncheckedIndexedAccess` can't see either invariant across the props
  // boundary, so narrow explicitly instead of a non-null assertion.
  const quotedMonthlyPayment = plan.entries[1]?.payment;
  const extendedMonthlyPayment = extendedPlan.entries[1]?.payment;
  const showMonthlyPayment =
    !isBullet &&
    quotedMonthlyPayment !== undefined &&
    extendedMonthlyPayment !== undefined;

  // The delta reads the two plans' TOTALS, so the arithmetic is the same
  // whatever the method. So is the tooltip: every method carries the extra
  // months at the rate the offer implies, so one string covers them all --
  // same single basis the banner states.
  const extraInterestBox = (
    <StatBox
      label={t("extended.costIncrease.extraInterest")}
      value={formatCurrency(
        Number(extendedPlan.totals.totalInterest) -
          Number(plan.totals.totalInterest),
      )}
      tooltip={t("extended.costIncrease.extraInterestTooltip")}
    />
  );
  // Δ total repaid − Δ interest == administration_fees × (extended − quoted
  // months), exactly. So with no recurring administration fee the two boxes are
  // numerically identical and the second one is pure noise; with one they differ
  // by real money (696 at 29/mo over 24 extra months, 10 260 at 95/mo over 108).
  // Gate on hasAdministrationFee alone, NOT the display hook's `hasFees`, which
  // also counts the setup fee — that is charged once either way and so cancels
  // out of the delta entirely.
  const showExtraTotalCost = extendedPlan.hasAdministrationFee;
  const extraTotalCostBox = showExtraTotalCost ? (
    <StatBox
      label={t("extended.costIncrease.extraTotalCost")}
      value={formatCurrency(
        Number(extendedPlan.totals.totalRepaid) -
          Number(plan.totals.totalRepaid),
      )}
      tooltip={t("extended.costIncrease.extraTotalCostTooltip")}
    />
  ) : null;

  return (
    <div className="mb-4" data-testid="extended-cost-increase">
      {/* One sentence, so a plain word space rather than a flex gap: the two
          halves read as "Extra costs compared to the N months quoted", and the
          gap-2 this used to sit in looked like a missing word. Inline flow also
          lets it wrap as a sentence instead of as two flex items. */}
      <Text className="mb-1 text-label">
        {t("extended.costIncrease.title")}{" "}
        <Text htmlTag="span" className="text-helper">
          {t("extended.costIncrease.versusQuoted", { months: quotedMonths })}
        </Text>
      </Text>
      <div className="flex flex-wrap gap-2">
        {showMonthlyPayment ? (
          <>
            <StatBox
              label={t("extended.costIncrease.monthlyPayment")}
              value={`${formatCurrency(
                Number(extendedMonthlyPayment),
              )} (${formatCurrency(Number(quotedMonthlyPayment))})`}
              tooltip={t("extended.costIncrease.monthlyPaymentTooltip")}
            />
            {extraInterestBox}
            {extraTotalCostBox}
          </>
        ) : (
          <>
            {extraTotalCostBox}
            {extraInterestBox}
          </>
        )}
      </div>
    </div>
  );
};

// KREA-4474 review-fix wave: extracted out of renderBody for the same reason
// ExtendedCostIncreaseSummary above was -- this cluster (the input/read-only
// months swap, the switch, validation, the "simulation unavailable" message,
// and the banner+caveat+summary) is where the wave's accessibility wiring
// and error-surfacing addition tipped renderBody's cognitive complexity over
// Biome's gate (it was already sitting at the limit before this wave).
const RepaymentTermControls = ({
  fullMonths,
  months,
  quotedMonths,
  isExtended,
  isBullet,
  extendedTerm,
  monthsValue,
  monthsError,
  methodCaveatKey,
  plan,
  formatCurrency,
  lenderName,
  t,
  onMonthsChange,
  commitMonths,
  onSelectedMonthsChange,
  onSwitchToggle,
}: {
  fullMonths: number;
  months: number;
  quotedMonths: number;
  isExtended: boolean;
  isBullet: boolean;
  extendedTerm?: IExtendedTermState;
  monthsValue: string;
  monthsError: boolean;
  /** Null unless the method needs a caveat beyond `banner` -- only
   * BULLET/BUILDING_CREDIT does. */
  methodCaveatKey: string | null;
  plan: IRepaymentPlanData;
  formatCurrency: (value: string | number) => string;
  lenderName: string | null;
  t: TFunction;
  onMonthsChange: (value: string) => void;
  commitMonths: () => void;
  onSelectedMonthsChange: (value: number) => void;
  onSwitchToggle: (checked: boolean) => void;
}) => {
  const monthsList = Array.from({ length: fullMonths }, (_, i) => i + 1);
  // KREA-4474 duplicate-id fix: multiple RepaymentPlanSection instances can be
  // on screen at once (e.g. CompanyLoanOffers maps sortedLenderResponses into
  // one card per bid), and both AccordionItem and BidsListItemDesktop keep a
  // collapsed card's DOM mounted (they animate opacity/height rather than
  // unmounting) -- so every instance's controls exist in the DOM
  // simultaneously, not just the expanded one. A hardcoded id here would
  // collide across instances exactly like AccordionItem's own `resolvedId`
  // comment (packages/shared/design-system/AccordionItem.tsx) warns about;
  // useId() keeps each instance's ids unique the same way.
  const instanceId = useId();
  const switchId = `extended-switch-${instanceId}`;
  const monthsErrorId = `extended-months-error-${instanceId}`;
  // A committed longer term produced no plan -- either the backend's own R6
  // null-by-design (non-convergence, or a bid with no plan) or a fetch error
  // (the query now rethrows rather than swallowing). Gated on the COMMITTED
  // `extendedTerm.months` exceeding `minMonths`, not the `months` prop above:
  // while extended with no plan, the caller's `activePlan` falls back to the
  // quoted `plan`, so `months` reads as the quoted term regardless of what
  // was actually requested and could never distinguish "failed" from "not
  // asked yet".
  const showExtendedUnavailable =
    isExtended &&
    extendedTerm !== undefined &&
    !extendedTerm.isLoading &&
    !extendedTerm.plan &&
    extendedTerm.months > extendedTerm.minMonths;

  return (
    <div className="mb-4">
      <div className="mb-1 flex flex-wrap items-baseline gap-2">
        <Text className="text-label">{t("slider.label")}:</Text>

        {isExtended ? (
          <span className="flex items-baseline gap-1">
            <Input
              data-testid="extended-months-input"
              type="number"
              className="w-20"
              aria-label={t("extended.inputLabel")}
              aria-invalid={monthsError}
              aria-describedby={monthsError ? monthsErrorId : undefined}
              min={extendedTerm?.minMonths}
              max={extendedTerm?.maxMonths}
              value={monthsValue}
              onChange={(event) => onMonthsChange(event.target.value)}
              onBlur={commitMonths}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  commitMonths();
                }
              }}
            />
            <Text className="text-base-strong">
              {t("commons.month", { count: months })}
            </Text>
          </span>
        ) : (
          <Text className="text-base-strong">
            {months} {t("commons.month", { count: months })}
          </Text>
        )}

        <Text className="text-helper">
          ({t("slider.originalTerm")}: {quotedMonths}{" "}
          {t("commons.month", { count: quotedMonths })})
        </Text>

        {extendedTerm && (
          <span className="ml-auto flex items-center gap-2">
            {isExtended && extendedTerm.isLoading && (
              <Text className="text-helper">{t("extended.loading")}</Text>
            )}
            <Text htmlTag="label" htmlFor={switchId} className="text-label">
              {t("extended.switchLabel")}
            </Text>
            <Switch
              id={switchId}
              checked={extendedTerm.isOn}
              onCheckedChange={onSwitchToggle}
            />
          </span>
        )}
      </div>

      {isExtended && monthsError && (
        <Text
          id={monthsErrorId}
          role="alert"
          className="mb-2 block text-helper text-red"
        >
          {t("extended.validationRange", {
            min: extendedTerm?.minMonths,
            max: extendedTerm?.maxMonths,
          })}
        </Text>
      )}

      {showExtendedUnavailable && (
        <Text
          data-testid="extended-unavailable"
          className="mb-2 block text-helper"
        >
          {t("extended.unavailable")}
        </Text>
      )}

      {isExtended && extendedTerm?.plan && (
        <>
          <Alert
            variant="info"
            data-testid="extended-banner"
            // Alert hardcodes role="alert" (assertive), which would re-announce
            // this whole paragraph every time the simulated term changes, since
            // {{months}} is interpolated into it. This is persistent context,
            // not an interruption, so it goes back to the polite role.
            role="status"
          >
            <Alert.Description>
              <Text className="block text-helper">
                {t("extended.banner", {
                  months,
                  lenderName: lenderName ?? t("extended.lenderFallback"),
                })}
              </Text>
              {methodCaveatKey && (
                <div data-testid="extended-method-caveat">
                  <Text className="mt-1 block text-helper">
                    {t(methodCaveatKey)}
                  </Text>
                </div>
              )}
            </Alert.Description>
          </Alert>

          <ExtendedCostIncreaseSummary
            plan={plan}
            extendedPlan={extendedTerm.plan}
            quotedMonths={quotedMonths}
            isBullet={isBullet}
            formatCurrency={formatCurrency}
            t={t}
          />
        </>
      )}

      {!isExtended && (
        <div data-testid="repayment-months-slider">
          <LabeledSlider
            value={months}
            valuesList={monthsList}
            onChange={onSelectedMonthsChange}
            showMinMax={false}
          />
        </div>
      )}
    </div>
  );
};

export const RepaymentPlanSection = ({
  bid,
  plan,
  currency,
  formatCurrency,
  formatAxisCurrency,
  formatPaybackTime,
  companyName,
  loanApplicationId,
  lenderName,
  extendedTerm,
}: IRepaymentPlanSectionProps) => {
  const { t } = useTranslation("repaymentPlan");
  const [selectedMonths, setSelectedMonths] = useState<number | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [planView, setPlanView] = useState("table");

  const isExtended = extendedTerm?.isOn === true;
  // Matched on the plan's own amortization method rather than a display name,
  // so it does not break when copy changes. Only BULLET/BUILDING_CREDIT needs
  // a caveat of its own: its payment is interest-only and so does not move
  // with the term at all, which `banner` would otherwise imply it does. Every
  // other method is fully described by `banner`, which already states that
  // interest is recalculated at the rate the offer implies -- so they add
  // nothing here rather than repeating that sentence.
  const methodCaveatKey: string | null =
    plan.amortizationMethod === "BULLET" ||
    plan.amortizationMethod === "BUILDING_CREDIT"
      ? "extended.bannerBullet"
      : null;
  // While extended, the fetched plan REPLACES the quoted one. Passing it with
  // months === its own full term makes the display hook's clamp a no-op and
  // leaves isEarly false, so the early-repayment savings block hides itself.
  // That is why neither useRepaymentPlanDisplay nor earlyRepayment.ts changes.
  const activePlan = (isExtended && extendedTerm?.plan) || plan;
  const fullMonths = activePlan.entries.length - 1;
  // The lender's own term. NOT fullMonths, which follows activePlan and so
  // becomes the simulated term while the switch is on.
  const quotedMonths = plan.entries.length - 1;
  // BULLET/BUILDING_CREDIT: the monthly payment is interest-only and never
  // changes with term, so the cost-increase summary omits that box entirely.
  const isBullet =
    plan.amortizationMethod === "BULLET" ||
    plan.amortizationMethod === "BUILDING_CREDIT";
  const months =
    isExtended || selectedMonths === null
      ? fullMonths
      : Math.min(Math.max(selectedMonths, 1), fullMonths);

  const display = useRepaymentPlanDisplay(activePlan, months);

  // Local draft so the month value commits on blur/Enter rather than on every
  // keystroke: each commit is a network round trip whose resolver runs a
  // promotions query plus the rate solve (up to ~11 calculator passes, each
  // with its own 80-iteration bisection at prec=50 for annuity).
  //
  // Deliberately NOT debounced. A debounce still fires on every pause while
  // someone types "1", "12", "120" -- three requests for two intermediate
  // values nobody asked to see -- and adds latency to the one value they did.
  // Commit-on-blur/Enter is one request per decision. Typing, holding an arrow
  // key and clicking the number spinner all mutate the draft only.
  // null means "not locally edited" -- show the committed term. This is what
  // makes a mount with isOn already true (a remount while extended, e.g. a
  // tab switch above this component) show the real committed months instead
  // of a blank input: seeding the draft only from the Switch's own
  // onCheckedChange handler left it stuck at "" for any mount that didn't
  // fire that handler in this component instance.
  const [monthsDraft, setMonthsDraft] = useState<string | null>(null);
  const [monthsError, setMonthsError] = useState(false);
  const monthsValue = monthsDraft ?? String(extendedTerm?.months ?? "");

  // Typing commits on a debounce so the admin never has to click away to see
  // the result. Blur and Enter still commit immediately, and both cancel any
  // pending timer so a value can't land twice.
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const cancelPendingCommit = () => {
    if (debounceRef.current !== null) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
  };
  // Cleanup is inlined rather than reusing cancelPendingCommit: that helper is
  // rebuilt every render, so depending on it would either re-run this effect
  // on every render or need a lint suppression. A ref read has no dependencies.
  useEffect(
    () => () => {
      if (debounceRef.current !== null) {
        clearTimeout(debounceRef.current);
      }
    },
    [],
  );

  // `value` is passed in rather than read from `monthsValue`: the debounced
  // caller is a closure from the render BEFORE setMonthsDraft lands, so
  // reading state here would commit the previous keystroke.
  //
  // `showError` separates the two paths. On an explicit commit an out-of-range
  // value has to say so; mid-typing it must not, or every keystroke toward a
  // valid term flashes the validation line ("3" on the way to "36" is below
  // the quoted minimum and would light up red).
  const commitTerm = (value: string, showError: boolean) => {
    const parsed = Number(value);
    const min = extendedTerm?.minMonths ?? 1;
    const max = extendedTerm?.maxMonths ?? 120;
    if (!Number.isInteger(parsed) || parsed < min || parsed > max) {
      if (showError) {
        setMonthsError(true);
      }
      return;
    }
    setMonthsError(false);
    // A valid value -- committed already or about to be -- follows the
    // committed term again rather than staying pinned to what was typed.
    setMonthsDraft(null);
    // Blur fires whenever focus leaves, including when nothing was typed, so
    // skip an unchanged value rather than churning container state.
    if (parsed === extendedTerm?.months) {
      return;
    }
    extendedTerm?.onMonthsCommit(parsed);
  };

  const commitMonths = () => {
    cancelPendingCommit();
    commitTerm(monthsValue, true);
  };

  const earlyRepaymentFeeLabel =
    bid.isRepayableWithoutFee || !bid.earlyRepaymentFee
      ? t("bids.common.repayableWithoutFee")
      : /^\d+(\.\d+)?$/.test(bid.earlyRepaymentFee.trim())
        ? formatCurrency(Number(bid.earlyRepaymentFee))
        : bid.earlyRepaymentFee;

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const { exportToXlsx } = await import("@krea/shared/utils/exportToXlsx");
      await exportToXlsx(
        buildRepaymentPlanExportSpec({
          bid,
          companyName,
          loanApplicationId,
          lenderName,
          earlyRepaymentFeeLabel,
          formatCurrency,
          formatPaybackTime,
          t,
          displayPlan: display.displayPlan,
          moneyColumns: display.moneyColumns,
          cellValue: display.cellValue,
          totalValue: display.totalValue,
          hasFees: display.hasFees,
          // Marked iff the plan actually IN VIEW is the simulated one, not
          // merely "the switch is on". On the first flip (switch on, nothing
          // committed yet) or during the loading window, `extendedTerm.plan`
          // is still null and the table is rendering the real quoted `plan`
          // -- exporting THAT unmarked-as-simulated would put the "not quoted
          // by the lender" notice over the lender's actual offer.
          extendedMonths:
            isExtended && extendedTerm?.plan ? extendedTerm.months : null,
        }),
      );
    } catch (error) {
      console.error("Failed to export amortisation plan", error);
    } finally {
      setIsExporting(false);
    }
  };

  const renderBody = () => {
    const { displayPlan, hasFees, moneyColumns, cellValue, totalValue } =
      display;

    const chartRows: IRepaymentChartRow[] = displayPlan.entries.map(
      (entry) => ({
        month: entry.month,
        principal: Number(entry.principal),
        interest: Number(entry.interest),
        fees: cellValue(entry, "fees"),
        balance: Number(entry.remainingBalance),
      }),
    );

    return (
      <div data-testid="repayment-plan-section">
        <Tabs value={planView} onValueChange={setPlanView}>
          <div className="mb-3 flex items-center justify-between gap-3">
            <TabsList>
              <TabsTrigger value="table">{t("tabs.table")}</TabsTrigger>
              <TabsTrigger value="graph">{t("tabs.graph")}</TabsTrigger>
            </TabsList>
            {planView === "table" && (
              <Button
                variant="secondary"
                size="sm"
                onClick={handleExport}
                disabled={isExporting}
                className="shrink-0"
              >
                <FileSpreadsheet className="h-4 w-4" />
                {t(isExporting ? "exporting" : "exportToExcel")}
              </Button>
            )}
          </div>

          {fullMonths > 1 && (
            <RepaymentTermControls
              fullMonths={fullMonths}
              months={months}
              quotedMonths={quotedMonths}
              isExtended={isExtended}
              isBullet={isBullet}
              extendedTerm={extendedTerm}
              monthsValue={monthsValue}
              monthsError={monthsError}
              methodCaveatKey={methodCaveatKey}
              plan={plan}
              formatCurrency={formatCurrency}
              lenderName={lenderName}
              t={t}
              onMonthsChange={(value) => {
                setMonthsDraft(value);
                // Clear a stale validation message the moment the user
                // edits again, rather than leaving the red line up while
                // they type a now-valid value.
                setMonthsError(false);
                cancelPendingCommit();
                debounceRef.current = setTimeout(() => {
                  debounceRef.current = null;
                  commitTerm(value, false);
                }, MONTHS_COMMIT_DEBOUNCE_MS);
              }}
              commitMonths={commitMonths}
              onSelectedMonthsChange={setSelectedMonths}
              onSwitchToggle={(checked) => {
                // Follow the committed term again rather than pinning to
                // whatever was mid-edit before the flip.
                setMonthsDraft(null);
                setMonthsError(false);
                extendedTerm?.onToggle(checked);
              }}
            />
          )}

          <TabsContent value="table">
            {/* h-96 matches the chart height so switching tabs doesn't shift
                the layout. */}
            <div className="h-96 overflow-auto rounded-lg border border-gray-200">
              <table className="w-full border-separate border-spacing-0 text-sm">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="text-table-header sticky top-0 z-10 bg-gray-100 py-3 pl-3 pr-2 text-left">
                      {t("month")}
                    </th>
                    {moneyColumns.map((key) => (
                      <th
                        key={key}
                        className="text-table-header sticky top-0 z-10 min-w-[120px] bg-gray-100 py-3 pr-2 text-right"
                      >
                        {t(key)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {displayPlan.entries.map((entry) => (
                    <tr
                      key={entry.month}
                      data-testid="repayment-row"
                      className="bg-white transition-colors hover:bg-primary-150"
                    >
                      <td className="border-t py-2 pl-3 pr-2 text-table-cell">
                        {entry.month}
                      </td>
                      {moneyColumns.map((key) => (
                        <td key={key} className="border-l border-t p-1">
                          <span className="block w-full px-1 py-1 text-right text-table-cell tabular-nums">
                            {formatCurrency(cellValue(entry, key))}
                          </span>
                        </td>
                      ))}
                    </tr>
                  ))}

                  <tr className="bg-gray-100 transition-colors hover:bg-gray-200">
                    <td className="border-t py-2 pl-3 pr-2 text-table-cell font-semibold">
                      {t("total")}
                    </td>
                    {moneyColumns.map((key) => {
                      const total = totalValue(key);
                      return (
                        <td key={key} className="border-l border-t p-1">
                          <span className="block w-full px-1 py-1 text-right text-table-cell font-semibold tabular-nums">
                            {total === null ? "" : formatCurrency(total)}
                          </span>
                        </td>
                      );
                    })}
                  </tr>
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="graph">
            <Suspense
              fallback={
                <Text className="text-helper block py-2">{t("loading")}</Text>
              }
            >
              <RepaymentPlanChart
                rows={chartRows}
                hasFees={hasFees}
                formatCurrency={formatCurrency}
                formatAxisCurrency={formatAxisCurrency}
                currency={currency}
              />
            </Suspense>
          </TabsContent>
        </Tabs>

        <div className="mt-4">
          <Text className="text-base-strong mb-2 block">
            {t("totalsTitle")}
          </Text>
          <div className="flex flex-wrap gap-3">
            <StatBox
              label={t("setupFee")}
              value={formatCurrency(Number(displayPlan.totals.setupFee))}
              tooltip={t("tooltips.setupFee")}
            />
            <StatBox
              label={t("totalCostOfCredit")}
              value={formatCurrency(
                Number(displayPlan.totals.totalCostOfCredit),
              )}
              tooltip={t("tooltips.totalCostOfCredit")}
            />
            <StatBox
              label={t("totalRepaid")}
              value={formatCurrency(Number(displayPlan.totals.totalRepaid))}
              tooltip={t("tooltips.totalRepaid")}
            />
          </div>
        </div>

        {displayPlan.isEarly && (
          <div
            className="-mx-4 -mb-3 mt-4 rounded-b-xl bg-mint-100 px-4 py-4 md:-mx-6 md:-mb-7 md:px-6"
            data-testid="repayment-savings"
          >
            <Text className="text-base-strong mb-1 block">
              {t("savings.title")}
            </Text>
            <Text className="text-helper mb-3 block">
              {t("savings.repaidEarlier", {
                duration: formatPaybackTime(displayPlan.monthsSaved),
              })}
            </Text>
            <div className="flex flex-wrap gap-3">
              <StatBox
                label={t("savings.interestSaved")}
                value={formatCurrency(displayPlan.interestSaved)}
                tooltip={t("tooltips.interestSaved")}
              />
              {hasFees && (
                <StatBox
                  label={t("savings.feesSaved")}
                  value={formatCurrency(displayPlan.feesSaved)}
                  tooltip={t("tooltips.feesSaved")}
                />
              )}
              <StatBox
                label={t("savings.grossSaved")}
                value={formatCurrency(displayPlan.grossSaved)}
                tooltip={t("tooltips.grossSaved")}
              />
              <StatBox
                label={t("savings.earlyRepaymentFee")}
                value={earlyRepaymentFeeLabel}
                tooltip={t("tooltips.earlyRepaymentFee")}
              />
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <AccordionItem
      title={
        <span className="flex flex-col gap-1">
          <Text htmlTag="span" className="text-subsection">
            {t("title")}
          </Text>
          <Text htmlTag="span" className="text-helper font-normal">
            {t("disclaimer")}
          </Text>
        </span>
      }
      titleStyling="mb-0"
      className="mt-4 rounded-xl bg-white"
      separatorWhenOpen
    >
      {renderBody()}
    </AccordionItem>
  );
};
